import CloseIcon from "@mui/icons-material/Close";
import ExploreIcon from "@mui/icons-material/Explore";
import StorageIcon from "@mui/icons-material/Storage";
import { CircularProgress, Dialog, IconButton } from "@mui/material";
import type { ComponentType } from "react";

import {
  CLIENT_TOOL_MODULES,
  type ClientTool,
} from "@/shared/domain/agentThread/clientTools";
import { resolveApprovalMode } from "@/shared/domain/agentThread/clientTools/approvalModes";
import { useClientToolPreferences } from "@/shared/domain/agentThread/hooks/useClientToolPreferences";
import { useUpdateClientToolPreference } from "@/shared/domain/agentThread/hooks/useUpdateClientToolPreference";
import { Button } from "@/shared/foundation/primitives";

import styles from "./ClientToolSettingsModal.module.css";
import { ToolApprovalModeSelector } from "./ToolApprovalModeSelector";

// ---------------------------------------------------------------------------
// Tool categories
// ---------------------------------------------------------------------------

interface ToolCategory {
  id: string;
  name: string;
  Icon: ComponentType<{ className?: string }>;
  toolNames: string[];
}

const CATEGORIES: ToolCategory[] = [
  {
    id: "nav",
    name: "Navigation & Views",
    Icon: ExploreIcon,
    toolNames: [
      "client_navigate",
      "client_visualize_open_panel",
      "client_visualize_add_to_panel",
      "client_visualize_remove_from_panel",
      "client_visualize_remove_panel",
      "client_visualize_search_topic",
      "client_visualize_open_file",
      "client_visualize_seek",
    ],
  },
  {
    id: "data",
    name: "Data & Events",
    Icon: StorageIcon,
    toolNames: ["client_create_events"],
  },
];

/**
 * Resolve category → tool modules from the registry. Tools not listed
 * in any category are appended to the last category.
 */
function getCategorizedTools(): {
  category: ToolCategory;
  tools: ClientTool[];
}[] {
  const categorizedNames = new Set(CATEGORIES.flatMap((c) => c.toolNames));

  const uncategorized = CLIENT_TOOL_MODULES.filter(
    (t) => !categorizedNames.has(t.spec.name),
  );

  const result = CATEGORIES.map((category) => ({
    category,
    tools: category.toolNames
      .map((name) => CLIENT_TOOL_MODULES.find((t) => t.spec.name === name))
      .filter((t): t is ClientTool => t !== undefined),
  })).filter((entry) => entry.tools.length > 0);

  // Append any uncategorized tools to the last category
  if (uncategorized.length > 0 && result.length > 0) {
    result[result.length - 1].tools.push(...uncategorized);
  }

  return result;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ClientToolSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Modal for configuring per-tool approval modes.
 *
 * Tools are grouped into categories. Each tool has a 3-way segmented
 * control for selecting its approval mode. Changes are persisted
 * immediately via optimistic update.
 */
export function ClientToolSettingsModal({
  open,
  onClose,
}: ClientToolSettingsModalProps) {
  const { data: preferences, isLoading } = useClientToolPreferences();
  const { updatePreference, resetPreferences } =
    useUpdateClientToolPreference();

  const categorizedTools = getCategorizedTools();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>Tool Permissions</h2>
          <p className={styles.subtitle}>
            Control how AI tools execute actions in your workspace.
          </p>
        </div>
        <IconButton
          aria-label="Close tool settings"
          onClick={onClose}
          size="small"
          className={styles.closeButton}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Body */}
      <div className={styles.body}>
        {isLoading && (
          <div className={styles.loading}>
            <CircularProgress size="1.5rem" />
          </div>
        )}

        {!isLoading &&
          categorizedTools.map(({ category, tools }) => (
            <div key={category.id} className={styles.category}>
              <div className={styles.categoryHeader}>
                <category.Icon className={styles.categoryIcon} />
                {category.name}
              </div>

              <div className={styles.toolList}>
                {tools.map((tool) => {
                  const effectiveMode = resolveApprovalMode(tool, preferences);

                  return (
                    <div key={tool.spec.name} className={styles.toolRow}>
                      <div className={styles.toolInfo}>
                        <span className={styles.toolName}>
                          {tool.displayName}
                        </span>
                        <span className={styles.toolDescription}>
                          {tool.displayDescription}
                        </span>
                      </div>
                      <ToolApprovalModeSelector
                        selectedMode={effectiveMode}
                        allowableModes={tool.allowableApprovalModes}
                        onChange={(mode) =>
                          updatePreference(tool.spec.name, mode)
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <button
          type="button"
          className={styles.resetButton}
          onClick={resetPreferences}
        >
          Reset to defaults
        </button>
        <Button variant="contained" onClick={onClose} size="small">
          Done
        </Button>
      </div>
    </Dialog>
  );
}
